import { useEffect, useState } from 'react';
import { useAppStore } from '@/stores/appStore';

/**
 * Honour both the in-app reduce-motion preference (`appStore.reduceMotion`)
 * and the OS-level `prefers-reduced-motion: reduce` media query.
 *
 * Returning `true` means the caller should skip non-essential motion —
 * swap animated media for a still, drop CSS keyframes, etc. Pair this
 * with the `[data-reduce-motion="true"]` document attribute (set by
 * `FluentProvider`) when expressing the same intent in CSS, so both
 * code paths react to a single source of truth.
 *
 * SSR-safe: returns `false` until `useEffect` runs.
 */
export function usePrefersReducedMotion(): boolean {
  const reduceMotion = useAppStore((s) => s.reduceMotion);
  const [systemReduced, setSystemReduced] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    setSystemReduced(mql.matches);
    const handler = (e: MediaQueryListEvent) => setSystemReduced(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  return reduceMotion === 'always' || systemReduced;
}
